import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../../core/cart";
import Header from "../../../components/common/header";
import NoCart from "./components/no-cart";
import { nanoid } from "nanoid";
import { gql } from "../../../__generated__";
import { useMutation, useQuery } from "@apollo/client";
import TransitionWrapper from "../../../components/common/transition-wrapper";
import FadeInWrapper from "../../../components/common/fade-in-wrapper";
import PickUpTimeBottomSheet from "../pickup-time-bottom-sheet";
import { track } from "@amplitude/analytics-browser";
import KakaoLogo from "../../../assets/images/kakao-logo.png";
import NaverLogo from "../../../assets/images/naver-logo.png";
import { formatPhoneNumber } from "../../../utils/phone";
import { UserCoupon } from "../../../__generated__/graphql";
import { ME_QUERY } from "../../../api/user-queries";
import PaymentLoading from "../../../components/payment/payment-loading";
import BACK_IMG from "../../../components/common/back-img.png";
import { notifyError } from "../../../utils/toast";
import { isKoreaDateOnOrAfterCutoff } from "../../../utils/payment-pg-cutoff";
import CouponSelectBottomSheet from "../../../components/checkout/coupon-select-bottom-sheet";

const CREATE_ORDER = gql(`
mutation CreateOrder($input: CreateOrderInput!) {
  createOrder(input: $input) {
    id
    orderId
    amount
    sellerAmount
    coupon
    products {
      id
      name
    }
    createdAt
    updatedAt
  }
}
`);

export const AVAILABLE_USER_COUPONS_QUERY = gql(`
  query AvailableUserCoupons {
    availableUserCoupons {
      id
      userId
      couponId
      isUsed
      expiredAt
      coupon {
        id
        title
        discountType
        discountValue
        minOrderPrice
        maxDiscountAmount
        storeId
      }
    }
  }
`);
const CheckoutLiveOrder: React.FC = () => {
  const navigate = useNavigate();
  const { cart, removeFromCart } = useCart(); // useCart 훅을 사용하여 카트 정보를 가져옵니다
  const [createOrder] = useMutation(CREATE_ORDER);

  // 사용자 정보를 가져오는 쿼리
  const { data, loading: userLoading } = useQuery(ME_QUERY, {
    fetchPolicy: "no-cache",
  });
  const { data: availableUserCouponsData } = useQuery(
    AVAILABLE_USER_COUPONS_QUERY
  );
  const userPhone = data?.me?.phone;
  const [selectedPayment, setSelectedPayment] = React.useState("kakaopay");
  const usesTossForLive = isKoreaDateOnOrAfterCutoff(new Date());

  const inputCart = cart.map((item) => {
    // typename왜 넣냐고 시비걸어서 그냥 필터링함
    return {
      product: { id: item?.product?.id as number },
      quantity: item.quantity,
    };
  });

  const [totalAmount, setTotalAmount] = React.useState(0);
  const [totalSellerAmount, setTotalSellerAmount] = React.useState(0);

  // useEffect를 사용하여 cart가 변경될 때마다 totalAmount 업데이트
  useEffect(() => {
    const newTotal = cart.reduce((total, item) => {
      const price = item.product.userPrice ?? item.product.price;
      return total + price * item.quantity;
    }, 0);
    setTotalAmount(newTotal);

    const newTotalSeller = cart.reduce((total, item) => {
      const price = Number(item.product?.discountPrice ?? 0);
      const safePrice = Number.isFinite(price) ? price : 0;
      return total + safePrice * item.quantity;
    }, 0);
    setTotalSellerAmount(newTotalSeller);
  }, [cart]);

  // getTotalAmount 함수는 이제 상태값을 반환
  const getTotalAmount = () => totalAmount;
  const getTotalSellerAmount = () => {
    return Number.isFinite(totalSellerAmount) ? totalSellerAmount : 0;
  };

  // removeFromCart 함수를 wrapping하여 analytics 추적 추가
  const handleRemoveFromCart = (productId: number) => {
    removeFromCart(productId);
    track("장바구니 상품 제거", {
      productId,
      remainingTotal: totalAmount,
    });
  };

  const getTotalQuantity = (): number => {
    return cart.reduce((total: number, item: { quantity: number }) => {
      const q = item.quantity;
      return total + q;
    }, 0);
  };

  const getTotalDisount = () => {
    return cart.reduce((total, item) => {
      const discount = item.product?.userPrice
        ? item.product.price - item?.product?.userPrice
        : 0;
      return total + discount * item.quantity;
    }, 0);
  };

  const handlePhoneChange = () => {
    localStorage.setItem("redirect", window.location.pathname);

    navigate("/phone-number-auth?allowBack=true");
  };

  const handleProceedClick = () => {
    track(
      usesTossForLive
        ? "결제하기(토스 결제 진행) 버튼 클릭"
        : "결제하기(나이스페이 호출) 버튼 클릭",
      {
        storeId: cart[0]?.store?.id,
        총액: getTotalAmount(),
        총수량: getTotalQuantity(),
        총할인: getTotalDisount(),
      },
    );
    if (!userPhone) {
      alert("연락처를 입력해주세요!");
      return;
    }
    setBottomSheetOpen(true);
  };

  const onClickProceed = async (time: string) => {
    try {
      const orderId = nanoid();
      const version = window.location.origin.includes("mahi.co.kr") ? "2" : "1";
      await createOrder({
        variables: {
          input: {
            totalQuantity: getTotalQuantity(),
            totalDiscount: getTotalDisount(),
            orderId,
            amount: getTotalAmount() - getCouponDiscountedAmount(),
            couponDiscountAmount: getCouponDiscountedAmount(),
            userCouponId: selectedCoupon?.id,
            baseAmount: getTotalAmount(),
            sellerAmount: getTotalSellerAmount(),
            productIds: cart.map((item) => item?.product?.id),
            pickUpTime: time,
            coupon: selectedCoupon?.coupon.title,
            ...(usesTossForLive
              ? {
                  pendingPaymentPayload: {
                    cartItems: inputCart,
                    version,
                  },
                }
              : {}),
          },
        },
      });

      const goodsName = `${cart[0]?.store?.title} 실시간 팩`;
      const payAmount = getTotalAmount() - getCouponDiscountedAmount();
      if (usesTossForLive) {
        navigate(
          `/payment/toss/widget?intent=live-order&orderId=${encodeURIComponent(
            orderId,
          )}&amount=${encodeURIComponent(
            String(payAmount),
          )}&orderName=${encodeURIComponent(goodsName)}`,
        );
        return;
      }

      if (!window.AUTHNICE) {
        throw new Error("결제 모듈을 불러올 수 없습니다.");
      }

      const clientId =
        process.env.REACT_APP_NODE_ENV === "dev"
          ? process.env.REACT_APP_NICE_PAY_CLIENT_KEY_TEST
          : version === "2"
            ? process.env.REACT_APP_NICE_PAY_CLIENT_KEY_V2
            : process.env.REACT_APP_NICE_PAY_CLIENT_KEY;

      const method =
        selectedPayment === "card"
          ? "card"
          : selectedPayment === "kakaopay"
            ? "kakaopay"
            : "naverpayCard";

      window.AUTHNICE.requestPay({
        clientId,
        method,
        orderId,
        amount: payAmount,
        appScheme: "mahi://",
        fnError: () => {
          setIsPaymentLoading(false);
          navigate("/nice-result?ok=0", { replace: true });
        },
        goodsName,
        returnUrl: `${process.env.REACT_APP_API_URL}/nice-auth`,
        mallReserved: JSON.stringify({
          cartItems: inputCart,
          version,
        }),
      });
    } catch (error) {
      notifyError(
        error instanceof Error ? error.message : "주문 생성에 실패했습니다.",
      );
    } finally {
      setIsPaymentLoading(false);
    }
  };

  const [isBottomSheetOpen, setBottomSheetOpen] = useState(false);
  const handlePickUpTimeConfirm = (time: string) => {
    const currentTime = new Date();
    const [selectedHour, selectedMinute] = time.split(":").map(Number);
    const selectedTime = new Date();
    selectedTime.setHours(selectedHour, selectedMinute, 0, 0);
    const closingTime = new Date();
    const [closingHour, closingMinute] = cart[0].store.closingHours
      .split(":")
      .map(Number);
    closingTime.setHours(closingHour, closingMinute, 0, 0);

    if (selectedTime <= currentTime) {
      alert("선택한 시간이 현재 시간보다 이전입니다. 다시 선택해주세요.");
      return;
    }

    if (selectedTime >= closingTime) {
      alert("마감시간을 넘습니다. 다시 선택해주세요.");
      return;
    }

    setIsPaymentLoading(true);
    onClickProceed(time);
  };

  const [selectedCoupon, setSelectedCoupon] = useState<UserCoupon | null>(null);
  const [isCouponSheetOpen, setIsCouponSheetOpen] = useState(false);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);

  const isCouponDisabled = (userCoupon: UserCoupon) => {
    const minOrderPrice = userCoupon.coupon.minOrderPrice;
    if (!minOrderPrice) return false;
    return getTotalAmount() < minOrderPrice;
  };

  // 매장 전용 쿠폰 필터링
  const currentStoreId = cart[0]?.store?.id;
  const filteredCoupons =
    availableUserCouponsData?.availableUserCoupons?.filter(
      (userCoupon: UserCoupon) => {
        // storeId가 없으면 전체 전용 쿠폰
        if (!userCoupon.coupon.storeId) {
          return true;
        }
        // storeId가 있으면 현재 매장과 일치하는지 확인
        return userCoupon.coupon.storeId === currentStoreId;
      }
    );

  const getCouponDiscountedAmount = () => {
    if (!selectedCoupon) return 0;

    if (selectedCoupon.coupon.discountType === "PERCENT") {
      return getTotalAmount() * (selectedCoupon.coupon.discountValue / 100);
    } else {
      return selectedCoupon.coupon.discountValue;
    }
  };

  const finalAmount = getTotalAmount() - getCouponDiscountedAmount();

  if (cart.length === 0) {
    return (
      <div className="w-full h-[100vh] flex flex-col">
        <Header title="장바구니" showBackButton />
        <NoCart />
      </div>
    );
  }

  return (
    <div className="bg-white p-4 flex flex-col min-h-screen relative">
      <FadeInWrapper className="flex flex-col pb-44">
        <img
          src={BACK_IMG}
          onClick={() => navigate(-1)}
          className="w-[40px] h-[40px] mb-4 cursor-pointer"
          alt="back"
        />
        <h1 className="text-3xl text-black font-extrabold mb-4">주문 하기</h1>

        <div className="flex flex-col space-y-8">
          {/* 주문 정보 */}
          <div>
            <h2 className="text-lg font-bold text-black mb-2">주문 정보</h2>
            <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
              <div className="flex flex-col space-y-4">
                {/* 매장 정보 및 마감 시간 */}
                <div className="flex justify-between items-center border-b border-gray-100 pb-3 mb-2">
                  <span className="font-bold text-lg text-black">
                    {cart[0]?.store?.title}
                  </span>
                  <span className="text-red-500 font-medium">
                    마감 {cart[0]?.store?.closingHours}
                  </span>
                </div>

                {/* 상품 리스트 */}
                <div className="flex flex-col space-y-4">
                  {cart.map((item, index) => (
                    <div key={index} className="flex gap-4 relative">
                      <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden border border-gray-100 bg-gray-50">
                        <img
                          src={
                            item.product.img || item.product?.menus?.[0]?.img
                          }
                          alt={item.product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex flex-col flex-grow justify-center min-w-0">
                        <div className="flex justify-between items-start">
                          <span className="text-black font-medium text-sm line-clamp-2 pr-6">
                            {item.product.name}
                          </span>
                          <button
                            onClick={() =>
                              handleRemoveFromCart(item.product.id)
                            }
                            className="text-gray-400 hover:text-gray-600 absolute right-0 top-0"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="w-5 h-5"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                        <div className="flex items-center text-gray-500 text-sm mt-1">
                          수량 {item.quantity}개
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-black font-semibold">
                            {(
                              item.quantity *
                              (item.product.userPrice || item.product.price)
                            ).toLocaleString()}
                            원
                          </span>
                          {item.product.userPrice && (
                            <span className="text-gray-400 text-xs line-through">
                              {(
                                item.quantity * item.product.price
                              ).toLocaleString()}
                              원
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* 연락처 */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-lg font-bold text-black">연락처</h2>
              <button
                onClick={userLoading ? undefined : handlePhoneChange}
                className={`text-sm text-gray-500 underline ${
                  userLoading ? "cursor-not-allowed opacity-50" : ""
                }`}
              >
                {userPhone ? "변경" : "등록"}
              </button>
            </div>
            <div
              className={`w-full rounded-xl px-4 h-12 flex items-center justify-center border ${
                userPhone
                  ? "bg-white border-gray-200"
                  : "bg-red-50 border-red-200"
              } shadow-sm`}
            >
              <div
                className={`text-base font-medium text-center ${
                  userPhone ? "text-black" : "text-red-500"
                }`}
              >
                {userLoading
                  ? "로딩 중..."
                  : userPhone
                  ? formatPhoneNumber(userPhone)
                  : "연락처를 등록해주세요"}
              </div>
            </div>
          </div>

          {/* 쿠폰 */}
          <div>
            <h2 className="text-lg font-bold text-black mb-2">쿠폰</h2>
            <button
              type="button"
              onClick={() => setIsCouponSheetOpen(true)}
              className="w-full px-4 min-h-12 py-3 bg-white border border-gray-200 rounded-xl shadow-sm text-left flex items-center justify-between gap-3 focus:outline-none focus:ring-2 focus:ring-[#1562fc] focus:border-transparent"
            >
              <div className="min-w-0">
                {selectedCoupon ? (
                  <>
                    <p className="text-black text-base font-medium truncate">
                      {selectedCoupon.coupon.title}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {selectedCoupon.coupon.discountType === "PERCENT"
                        ? `${selectedCoupon.coupon.discountValue}% 할인`
                        : `${Number(selectedCoupon.coupon.discountValue).toLocaleString("ko-KR")}원 할인`}
                      {selectedCoupon.expiredAt
                        ? ` · ${new Date(selectedCoupon.expiredAt).toLocaleDateString("ko-KR")}까지`
                        : ""}
                    </p>
                  </>
                ) : (
                  <p className="text-gray-500 text-base">사용할 쿠폰 선택</p>
                )}
              </div>
              <span className="text-gray-400 text-sm shrink-0">선택</span>
            </button>
          </div>

          {/* 결제수단 */}
          <div>
            <h2 className="text-lg font-bold text-black mb-2">결제수단</h2>
            {usesTossForLive ? (
              <p className="text-sm text-gray-600 rounded-xl border border-gray-200 p-4 bg-gray-50">
                다음 화면에서 카드·간편결제 등 결제 수단을 선택할 수 있어요.
              </p>
            ) : (
              <div className="flex flex-col space-y-3">
                <label
                  className={`flex items-center space-x-3 p-4 rounded-xl border cursor-pointer transition-all ${
                    selectedPayment === "kakaopay"
                      ? "bg-yellow-50 border-yellow-400"
                      : "bg-white border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="payment"
                    value="kakaopay"
                    checked={selectedPayment === "kakaopay"}
                    onChange={(event) => setSelectedPayment(event.target.value)}
                    className="w-5 h-5 accent-yellow-400"
                  />
                  <div className="flex items-center space-x-2">
                    <img src={KakaoLogo} alt="카카오페이" className="h-6" />
                    <span className="text-black font-medium">카카오페이</span>
                  </div>
                </label>

                <label
                  className={`flex items-center space-x-3 p-4 rounded-xl border cursor-pointer transition-all ${
                    selectedPayment === "naverpay"
                      ? "bg-green-50 border-green-500"
                      : "bg-white border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="payment"
                    value="naverpay"
                    checked={selectedPayment === "naverpay"}
                    onChange={(event) => setSelectedPayment(event.target.value)}
                    className="w-5 h-5 accent-green-600"
                  />
                  <div className="flex items-center space-x-2">
                    <img
                      src={NaverLogo}
                      alt="네이버페이"
                      className="h-6 rounded-lg"
                    />
                    <span className="text-black font-medium">네이버페이</span>
                  </div>
                </label>

                <label
                  className={`flex items-center space-x-3 p-4 rounded-xl border cursor-pointer transition-all ${
                    selectedPayment === "card"
                      ? "bg-blue-50 border-blue-500"
                      : "bg-white border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="payment"
                    value="card"
                    checked={selectedPayment === "card"}
                    onChange={(event) => setSelectedPayment(event.target.value)}
                    className="w-5 h-5 accent-blue-600"
                  />
                  <div className="flex items-center space-x-2">
                    <svg
                      className="w-6 h-6 text-gray-700"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3z"
                      />
                    </svg>
                    <span className="text-black font-medium">신용/체크카드</span>
                  </div>
                </label>
              </div>
            )}
          </div>
        </div>
      </FadeInWrapper>

      {/* Bottom Area */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 p-4 pb-8 z-20">
        <div className="max-w-xl mx-auto">
          {selectedCoupon && (
            <div className="flex justify-between items-center mb-1">
              <span className="text-gray-500 text-sm">할인 금액</span>
              <span className="text-red-500 font-bold">
                -{getCouponDiscountedAmount().toLocaleString()}원
              </span>
            </div>
          )}
          <div className="flex justify-between items-center mb-4">
            <span className="text-lg font-bold text-black">총 결제 금액</span>
            <span className="text-2xl font-extrabold text-[#1562fc]">
              {finalAmount.toLocaleString()}원
            </span>
          </div>

          <TransitionWrapper
            scale={0.95}
            opacity={0.8}
            onClick={userLoading ? undefined : handleProceedClick}
            className={`w-full h-[56px] flex items-center justify-center bg-[#1562fc] rounded-2xl shadow-lg shadow-blue-500/30 ${
              userLoading
                ? "opacity-50 cursor-not-allowed shadow-none bg-gray-400"
                : ""
            }`}
          >
            <span className="text-white text-lg font-bold">
              결제하고 가지러가기
            </span>
          </TransitionWrapper>
        </div>
      </div>

      <PickUpTimeBottomSheet
        isOpen={isBottomSheetOpen}
        onClose={() => setBottomSheetOpen(false)}
        onConfirm={handlePickUpTimeConfirm}
      />
      <CouponSelectBottomSheet
        isOpen={isCouponSheetOpen}
        onClose={() => setIsCouponSheetOpen(false)}
        coupons={filteredCoupons ?? []}
        selectedCouponId={selectedCoupon?.id ?? null}
        onSelect={setSelectedCoupon}
        isCouponDisabled={isCouponDisabled}
      />
      <PaymentLoading isVisible={isPaymentLoading} />
    </div>
  );
};

export default CheckoutLiveOrder;
